import { DEFAULT_PALLET_OPTIONS, PALLET_TYPE } from "constants/palletOptions.constants";
import { UNIT_SYSTEM } from "constants/units.contants";
import ExcelJS from "exceljs";
import { Group, PalletRow } from "models/packingList";
import { Unit } from "models/units";
import { toDisplayLength, toDisplayWeight } from "./units.utils";

const getPalletName = (palletRow: PalletRow, customPallets: PalletRow[]): string | null => {
  if (palletRow.palleteName) return palletRow.palleteName;

  const preset = DEFAULT_PALLET_OPTIONS.find((option) => option.value === palletRow.palletType);
  if (preset && preset.value !== PALLET_TYPE.CUSTOM_PALLETS) return preset.label;

  const savedPallet = customPallets.find((pallet) => pallet.id === palletRow.palletType);
  return savedPallet?.palleteName ?? null;
};

const COLUMNS = [
  { header: "Group", key: "group", width: 20 },
  { header: "Product Name (UID)", key: "product", width: 35 },
  { header: "Type", key: "type", width: 15 },
  { header: "Length/Diameter", key: "length", width: 16 },
  { header: "Width", key: "width", width: 12 },
  { header: "Height", key: "height", width: 12 },
  { header: "Weight", key: "weight", width: 12 },
  { header: "Quantity", key: "quantity", width: 12 },
  { header: "Pallet max weight", key: "palletMaxWeight", width: 16 },
  { header: "Pallet depth", key: "palletDepth", width: 14 },
  { header: "Units", key: "units", width: 10 },
];

const UNIT_SYSTEM_LABELS: Record<Unit, string> = {
  [UNIT_SYSTEM.METRIC]: "SI",
  [UNIT_SYSTEM.IMPERIAL]: "US",
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const exportPackingListXlsx = async (
  groups: Group[],
  customPallets: PalletRow[],
  unitSystem: Unit
): Promise<void> => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Packing list");
  worksheet.columns = COLUMNS;

  let isFirstRow = true;

  groups.forEach((group) => {
    const groupRow: Record<string, string | number | null> = { group: group.name };

    if (isFirstRow) {
      groupRow.units = UNIT_SYSTEM_LABELS[unitSystem];
      isFirstRow = false;
    }

    if (group.usePallets) {
      groupRow.product = getPalletName(group.palletRow, customPallets);
      groupRow.length = toDisplayLength(group.palletRow.length, unitSystem);
      groupRow.width = toDisplayLength(group.palletRow.width, unitSystem);
      groupRow.height = toDisplayLength(group.palletRow.height, unitSystem);
      groupRow.palletMaxWeight = toDisplayWeight(group.palletRow.maxWeight, unitSystem);
      groupRow.palletDepth = toDisplayLength(group.palletRow.depth, unitSystem);
    }

    worksheet.addRow(groupRow);

    group.cargoRows.forEach((cargoRow) => {
      worksheet.addRow({
        product: cargoRow.product,
        type: cargoRow.packageType,
        length: toDisplayLength(cargoRow.length, unitSystem),
        width: toDisplayLength(cargoRow.width, unitSystem),
        height: toDisplayLength(cargoRow.height, unitSystem),
        weight: toDisplayWeight(cargoRow.weight, unitSystem),
        quantity: cargoRow.qty,
      });
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();

  downloadBlob(
    new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
    `load-calculator-${new Date().toISOString().slice(0, 10)}.xlsx`
  );
};
